import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import axios from 'axios';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine
} from 'recharts';
import { API_URL } from '../../config';
import { tokens } from '../../utils/theme';
import { BtnIcon } from '../../components/SharedPageStyles';
import { copyTableAsJSON } from '../../utils/clipboardUtils';

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 20px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const CategoryTitle = styled.h4`
    font-size: 12px;
    font-weight: 600;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.12em;
`;

const ToggleGroup = styled.div`
    display: flex;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15, 23, 42, 0.1)'};
`;

const ToggleBtn = styled.button`
    background: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15,118,110,0.1)')
        : 'transparent'};
    color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border: none;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: ${props => props.active ? '600' : '500'};

    &:hover {
        background: ${props => !props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.25)' : 'rgba(15,118,110,0.15)')};
        color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#b2dfdb' : '#115e59')
        : (props.theme.palette.mode === 'dark' ? '#fff' : '#000')};
    }
`;

const SetupSummaryRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;
`;

const OpportunityChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.03)'};
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
`;

const MainGrid = styled.div`
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 24px;
    margin-bottom: 20px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const RankingSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    font-size: 0.85rem;
    padding: 4px 6px;
    border-radius: 4px;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.03);
    }
`;

const Ticker = styled.span`
    width: 42px;
    font-weight: 500;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const ChartArea = styled.div`
    flex-grow: 1;
    position: relative;
    height: 20px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 3px;
    display: flex;
    align-items: center;
    margin: 0 10px;
`;

const CenterLine = styled.div`
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.2);
    z-index: 1;
`;

const Bar = styled.div`
    position: absolute;
    height: 14px;
    top: 3px;
    border-radius: 2px;
    z-index: 2;
    transition: width 0.3s ease;

    ${props => props.isPositive ? `
        left: 50%;
        width: ${props.width}%;
        background-color: rgba(38, 166, 154, 0.8);
    ` : `
        right: 50%;
        width: ${props.width}%;
        background-color: rgba(239, 83, 80, 0.8);
    `}
`;

const ValueLabel = styled.div`
    width: 55px;
    text-align: right;
    font-weight: 500;
    font-size: 12px;
    color: ${props => props.isPositive
        ? tokens(props.theme.palette.mode).greenAccent[500]
        : (props.isNegative ? tokens(props.theme.palette.mode).redAccent[500] : tokens(props.theme.palette.mode).grey[400])};
`;

const ChartSection = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 380px;
`;

const ChartHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
`;

const LegendToggles = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

const CurrencyChip = styled.button`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 3px 7px;
    font-size: 11px;
    font-weight: 500;
    border-radius: 4px;
    border: 1px solid ${props => props.$visible ? 'rgba(255,255,255,0.15)' : 'transparent'};
    background: ${props => props.$visible ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
    color: ${props => props.$visible ? props.$color : tokens(props.theme.palette.mode).grey[500]};
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }
`;

// Sober, muted palette matching Trade Terminal themes (7 currencies from Market Overview)
const CURRENCY_COLORS = {
    EUR: '#64b5f6', // soft light blue
    USD: '#81c784', // soft green
    GBP: '#ba68c8', // soft lavender
    JPY: '#e57373', // soft red
    CHF: '#ffb74d', // soft amber
    CAD: '#4dd0e1', // soft cyan
    AUD: '#ff8a65'  // soft coral
};

const PERIODS = [
    { key: 'D', label: 'D' },
    { key: 'W', label: 'W' },
    { key: 'M', label: 'M' },
    { key: 'MTD', label: 'MTD' },
    { key: 'Q', label: 'Q' },
    { key: '6M', label: '6M' },
    { key: '1Y', label: 'Y' },
    { key: 'YTD', label: 'YTD' }
];

const CurrencyStrengthTab = () => {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePeriod, setActivePeriod] = useState('D');
    const [visibleLines, setVisibleLines] = useState({
        EUR: true,
        USD: true,
        GBP: true,
        JPY: true,
        CHF: true,
        CAD: true,
        AUD: true
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/intermarket/currency-strength`);
            setData(res.data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch currency strength:", err);
            setError("Failed to load currency strength data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleLine = (ccy) => {
        setVisibleLines(prev => ({
            ...prev,
            [ccy]: !prev[ccy]
        }));
    };

    const currentRankings = useMemo(() => {
        if (!data || !data.rankings) return [];
        return data.rankings[activePeriod] || data.rankings['D'] || [];
    }, [data, activePeriod]);

    const maxAbsScore = useMemo(() => {
        if (!currentRankings.length) return 1.0;
        const maxVal = Math.max(...currentRankings.map(r => Math.abs(r.score)));
        return maxVal > 0 ? maxVal : 1.0;
    }, [currentRankings]);

    const handleCopyJSON = () => {
        if (!currentRankings.length) return;
        copyTableAsJSON(currentRankings);
    };

    if (loading) {
        return (
            <Container>
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    Fetching currency strength data...
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <div style={{ padding: '20px', color: '#ef5350', background: 'rgba(239, 83, 80, 0.1)', borderRadius: '4px' }}>
                    {error}
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <ScrollableContent>
                <HeaderRow>
                    <CategoryTitle>CURRENCY STRENGTH</CategoryTitle>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ToggleGroup>
                            {PERIODS.map(p => (
                                <ToggleBtn
                                    key={p.key}
                                    active={activePeriod === p.key}
                                    onClick={() => setActivePeriod(p.key)}
                                >
                                    {p.label}
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>

                        <BtnIcon onClick={handleCopyJSON} title="Copy JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                    </div>
                </HeaderRow>

                {/* Top setups: sober, concise pill row */}
                {data?.recommendations && data.recommendations.length > 0 && (
                    <SetupSummaryRow>
                        {data.recommendations.map((rec, idx) => {
                            const isBuy = rec.bias === 'BUY';
                            const strongSym = rec.strongSymbol || rec.strongCurrency || '';
                            const weakSym = rec.weakSymbol || rec.weakCurrency || '';
                            let pairClean = rec.pair;
                            if (!pairClean || pairClean.includes('{') || pairClean.length !== 6) {
                                pairClean = isBuy ? `${strongSym}${weakSym}` : `${weakSym}${strongSym}`;
                            }
                            const spreadVal = rec.spread ? Math.abs(rec.spread).toFixed(2) : '0.00';

                            return (
                                <OpportunityChip key={idx}>
                                    <span style={{ fontWeight: 500 }}>{pairClean}</span>
                                    <span style={{
                                        color: isBuy
                                            ? tokens(theme.palette.mode).greenAccent[500]
                                            : tokens(theme.palette.mode).redAccent[500],
                                        fontWeight: 500,
                                        fontSize: '11px'
                                    }}>
                                        {rec.bias}
                                    </span>
                                    <span style={{ color: tokens(theme.palette.mode).grey[400], fontSize: '11px' }}>
                                        +{spreadVal}% ({strongSym} / {weakSym})
                                    </span>
                                </OpportunityChip>
                            );
                        })}
                    </SetupSummaryRow>
                )}

                <MainGrid>
                    {/* Left: Strength Rankings matching IntermarketChart bars */}
                    <RankingSection>
                        <CategoryTitle style={{ marginBottom: '12px' }}>
                            STRENGTH ({activePeriod})
                        </CategoryTitle>

                        {currentRankings.map((item) => {
                            const isPositive = item.score > 0;
                            const isNegative = item.score < 0;
                            const widthPercent = Math.min(50, (Math.abs(item.score) / maxAbsScore) * 50);

                            return (
                                <Row key={item.currency}>
                                    <Ticker>{item.currency}</Ticker>

                                    <ChartArea>
                                        <CenterLine />
                                        <Bar
                                            isPositive={isPositive}
                                            width={widthPercent}
                                        />
                                    </ChartArea>

                                    <ValueLabel isPositive={isPositive} isNegative={isNegative}>
                                        {item.score > 0 ? `+${item.score.toFixed(2)}%` : `${item.score.toFixed(2)}%`}
                                    </ValueLabel>
                                </Row>
                            );
                        })}
                    </RankingSection>

                    {/* Right: Soba Trajectory Line Chart */}
                    <ChartSection>
                        <ChartHeaderRow>
                            <CategoryTitle style={{ margin: 0 }}>
                                INDEX TRAJECTORY (BASE 100)
                            </CategoryTitle>

                            <LegendToggles>
                                {Object.keys(CURRENCY_COLORS).map(ccy => (
                                    <CurrencyChip
                                        key={ccy}
                                        $color={CURRENCY_COLORS[ccy]}
                                        $visible={visibleLines[ccy]}
                                        onClick={() => toggleLine(ccy)}
                                        title={`Toggle ${ccy}`}
                                    >
                                        <span style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            background: visibleLines[ccy] ? CURRENCY_COLORS[ccy] : 'rgba(255,255,255,0.2)'
                                        }} />
                                        {ccy}
                                    </CurrencyChip>
                                ))}
                            </LegendToggles>
                        </ChartHeaderRow>

                        <div style={{ flex: 1, width: '100%', minHeight: '340px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={data?.chartData || []}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <XAxis
                                        dataKey="date"
                                        stroke="rgba(255,255,255,0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255,255,255,0.2)"
                                        tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 11 }}
                                        tickLine={false}
                                        domain={['auto', 'auto']}
                                    />
                                    <ReferenceLine y={100} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.mode === 'dark' ? '#0c0e17' : '#ffffff',
                                            borderColor: 'rgba(255,255,255,0.1)',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            padding: '8px 12px'
                                        }}
                                        labelStyle={{ color: tokens(theme.palette.mode).grey[300], fontWeight: 500, marginBottom: 4 }}
                                    />
                                    {Object.keys(CURRENCY_COLORS).map(ccy => (
                                        visibleLines[ccy] && (
                                            <Line
                                                key={ccy}
                                                type="monotone"
                                                dataKey={ccy}
                                                stroke={CURRENCY_COLORS[ccy]}
                                                dot={false}
                                                strokeWidth={1.5}
                                                isAnimationActive={false}
                                            />
                                        )
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartSection>
                </MainGrid>
            </ScrollableContent>
        </Container>
    );
};

export default CurrencyStrengthTab;
